import React, { FC, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Tab,
  Tabs,
  Flex,
  Stack,
  Button,
  Heading,
  TabList,
  TabPanel,
  TabPanels,
} from '@chakra-ui/react';

import { HiDocumentAdd } from 'react-icons/hi';

import { washHub } from 'store';

import { useMutation, useQuery } from 'react-query';
import { getOrderCounts, exportOrdersCsv } from 'api/order';

import {
  AddOrderModal,
  AddOrderModalRef,
} from 'components/modules/AddOrderModal';

import { AllOrders } from './AllOrders';
import { OpenOrders } from './OpenOrders';
import { ClosedOrders } from './ClosedOrders';
import { PendingInvoiceOrders } from './PendingInvoiceOrders';

import { UserPill } from 'components/core/UserPill';
import { Filters } from 'components/primitives/Filters';
import { SearchInput } from 'components/primitives/SearchInput';

import DateStatCard from 'components/primitives/DateStatCard';

import { Status } from 'types/items';
import { OrdersViewProps } from './Orders.props';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';
import { routesSettings } from 'constants/routes';
import moment from 'moment';

const OrdersView: FC<OrdersViewProps> = (props) => {
  const {
    venue,
    status,
    endDate,
    startDate,
    isDeleting,
    defaultIndex,
    invoiced,
    accountManagers,
    feeAdjustmentTypes,
    setDefaultIndex,
    onDelete,
    onStatusClick,
    onSearchClear,
    onSearchPress,
    onApplyFilters,
    onClearFilters,
    searchInput,
    search,
    onSearchChange,
  } = props;

  const _focus = { border: 'none', backgroundColor: 'primary.500' };
  const _selected = { backgroundColor: 'primary.900', color: 'support.500' };

  const addOrderModalRef = useRef<AddOrderModalRef>(null);
  const navigate = useNavigate();

  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';

  const csvRef = useRef<any>(null);

  const { data: ordersCount } = useQuery(
    [
      `fetch-orders-status-count-${washHubId}`,
      startDate,
      endDate,
      venue?.toString(),
      invoiced,
      accountManagers?.toString(),
    ],
    () => getOrderCounts(washHubId, startDate, endDate, venue, invoiced),
  );

  const { mutate, data: csvData = '', isLoading: isExporting } = useMutation(
    async () => {
      let response: string = '';

      let status = undefined;
      switch (defaultIndex) {
        case 1:
          status = Status.OPEN;
          break;
        case 2:
          status = Status.PENDING_COUNT;
          break;
        case 3:
          status = Status.CLOSE;
          break;
        default:
          status = undefined;
          break;
      }

      response = await exportOrdersCsv({
        washHub: washHubId,
        status: status,
        endDate,
        startDate,
        venue,
        accountManagers,
      });

      return response;
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.click();
        }, 500);
      },
    },
  );

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isExporting}
          ml={2}
          minW="200px"
          onClick={() => {
            mutate();
          }}>
          Export Orders to CSV
        </Button>
        {Boolean(csvData) && (
          <a
            href={`data:text/csv;charset=utf-8,${encodeURIComponent(csvData)}`}
            download={`washhub-${selectedWashHub?.name}-orders.csv`}
            style={{ display: 'none' }}
            ref={csvRef}>
            Export CSV
          </a>
        )}
      </>
    );
  };

  const openOrders = ordersCount?.[Status.OPEN] || 0;
  const closeOrders = ordersCount?.[Status.CLOSE] || 0;
  const pendingInvoiceOders = ordersCount?.[Status.PENDING_INVOICE] || 0;

  const allOrders = openOrders + closeOrders + pendingInvoiceOders;

  const _props = {
    endDate,
    startDate,
    isDeleting,
    onDelete,
    venue,
    invoiced,
    accountManagers,
    feeAdjustmentTypes,
    search,
  };

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Flex align="flex-start" justify="space-between">
        <Heading mb={6} fontSize="24px">
          Orders
        </Heading>
        <Flex align="flex-end">
          <Stack isInline>
            {renderExpBtn()}
            <Button
              ml={2}
              minW="200px"
              onClick={() => {
                navigate(routesSettings.PORTAL_ORDERS_SUMMARY.getPath());
              }}>
              Orders Summary
            </Button>
            <Button
              isDisabled={washHubId === ''}
              mr={2}
              minW="200px"
              leftIcon={<HiDocumentAdd size="20px" />}
              onClick={() => {
                addOrderModalRef.current?.onOpen?.();
              }}>
              Create Order
            </Button>
            <UserPill />
          </Stack>
        </Flex>
      </Flex>
      <DateRangeInputExt
        startDate={startDate}
        endDate={endDate}
        onSearchClear={onSearchClear}
        onSearchPress={onSearchPress}
      />
      <DateStatCard status={status as any} onStatusClick={onStatusClick} />
      <Flex justify="space-between" align="flex-start" gap={2}>
        <Filters
          isFullButton
          isSitesOnly
          isAccountManagersOnly
          isInvoiced
          isFeeTypesOnly
          venue={venue || []}
          invoiced={invoiced}
          feeAdjustmentTypes={feeAdjustmentTypes}
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
        />
        <SearchInput
          placeholder="Search order no., site, or SKU"
          value={searchInput}
          onChange={onSearchChange}
        />
      </Flex>
      <Tabs
        variant="enclosed"
        size="md"
        index={defaultIndex || 0}
        defaultIndex={defaultIndex}
        onChange={(item) => {
          // setSearchParams({ currentStatus: item.toString() });
          setDefaultIndex(item);
        }}>
        <TabList _selected={_selected} _focus={_focus} w="100%">
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            All {`(${allOrders})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Open {`(${openOrders})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Pending Invoice {`(${pendingInvoiceOders})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Close {`(${closeOrders})`}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>
            <AllOrders {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <OpenOrders {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <PendingInvoiceOrders {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <ClosedOrders {..._props} />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <AddOrderModal ref={addOrderModalRef} />
    </Flex>
  );
};

export default OrdersView;
